import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, ShoppingCart, Info, ImageOff, Download, ExternalLink, Check, ShieldCheck, ZoomIn, X, ChevronLeft, ChevronRight, FileText } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { useCart } from "@/context/CartContext";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { productImage, productTitle, productDesc, formatPrice } from "@/lib/helpers";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { pageTitle, truncate, stripHtml, SITE_ORIGIN } from "@/lib/seo";

const ZoomablePage = ({ src, testid, alt }) => {
  const wrapRef = useRef(null);
  const imgRef = useRef(null);
  const st = useRef({ scale: 1, tx: 0, ty: 0, mode: null, d0: 0, s0: 1, x0: 0, y0: 0, tx0: 0, ty0: 0 });
  const apply = () => {
    const { scale, tx, ty } = st.current;
    if (imgRef.current) imgRef.current.style.transform = `translate(${tx}px, ${ty}px) scale(${scale})`;
    if (wrapRef.current) wrapRef.current.style.touchAction = scale > 1 ? "none" : "pan-y";
  };
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
    const start = (e) => {
      const s = st.current;
      if (e.touches.length === 2) { s.mode = "pinch"; s.d0 = dist(e.touches) || 1; s.s0 = s.scale; }
      else if (e.touches.length === 1 && s.scale > 1) { s.mode = "pan"; s.x0 = e.touches[0].clientX; s.y0 = e.touches[0].clientY; s.tx0 = s.tx; s.ty0 = s.ty; }
      else s.mode = null;
    };
    const move = (e) => {
      const s = st.current;
      if (s.mode === "pinch" && e.touches.length === 2) {
        e.preventDefault();
        let ns = s.s0 * (dist(e.touches) / s.d0);
        ns = Math.min(4, Math.max(1, ns));
        s.scale = ns;
        if (ns === 1) { s.tx = 0; s.ty = 0; }
        apply();
      } else if (s.mode === "pan" && e.touches.length === 1) {
        e.preventDefault();
        s.tx = s.tx0 + (e.touches[0].clientX - s.x0);
        s.ty = s.ty0 + (e.touches[0].clientY - s.y0);
        apply();
      }
    };
    const end = () => { const s = st.current; if (s.scale <= 1) { s.scale = 1; s.tx = 0; s.ty = 0; apply(); } s.mode = null; };
    el.addEventListener("touchstart", start, { passive: true });
    el.addEventListener("touchmove", move, { passive: false });
    el.addEventListener("touchend", end, { passive: true });
    el.addEventListener("touchcancel", end, { passive: true });
    return () => {
      el.removeEventListener("touchstart", start);
      el.removeEventListener("touchmove", move);
      el.removeEventListener("touchend", end);
      el.removeEventListener("touchcancel", end);
    };
  }, []);
  const toggleZoom = () => { const s = st.current; if (s.scale > 1) { s.scale = 1; s.tx = 0; s.ty = 0; } else { s.scale = 2; } apply(); };
  return (
    <div ref={wrapRef} data-testid={`${testid}-wrap`} onDoubleClick={toggleZoom} style={{ touchAction: "pan-y" }}
      className="overflow-hidden rounded-lg border border-brand-navy/10 shadow-sm bg-white">
      <img ref={imgRef} src={src} data-testid={testid} alt={alt} loading="lazy"
        onError={(e) => { e.currentTarget.style.display = "none"; }}
        style={{ transformOrigin: "center center", willChange: "transform" }}
        className="w-full h-auto select-none" />
    </div>
  );
};

const ProductDetail = () => {
  const { id } = useParams();
  const { t, lang } = useI18n();
  const { addItem } = useCart();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const [lbIdx, setLbIdx] = useState(0);
  const [zoomed, setZoomed] = useState(false);
  const touchStartX = useRef(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    setActiveIdx(0);
    api.get(`/products/${id}`).then(({ data }) => setProduct(data)).catch(() => setNotFound(true));
  }, [id]);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => { if (e.key === "Escape") setLightbox(false); };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = prevOverflow; };
  }, [lightbox]);

  useEffect(() => {
    if (!previewOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setPreviewOpen(false); };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = prevOverflow; };
  }, [previewOpen]);

  if (notFound) return <div className="max-w-7xl mx-auto px-4 py-24 text-center text-brand-navy/60">{t("products.empty")}</div>;
  if (!product) return <div className="max-w-7xl mx-auto px-4 py-24 text-center text-brand-navy/50">{t("common.loading")}</div>;

  const img = productImage(product, 1000);
  const galleryImgs = (product.gallery_images || []).map((g) => productImage(g, 1000)).filter(Boolean);
  const allImgs = [img, ...galleryImgs].filter(Boolean);
  const mainImg = allImgs[activeIdx] || img;
  const openLightbox = (i) => { setLbIdx(i); setZoomed(false); setLightbox(true); };
  const lbNext = (e) => { e?.stopPropagation(); setZoomed(false); setLbIdx((i) => (i + 1) % allImgs.length); };
  const lbPrev = (e) => { e?.stopPropagation(); setZoomed(false); setLbIdx((i) => (i - 1 + allImgs.length) % allImgs.length); };
  const onLbTouchStart = (e) => { touchStartX.current = e.changedTouches[0].clientX; };
  const onLbTouchEnd = (e) => {
    if (touchStartX.current == null || allImgs.length < 2 || zoomed) { touchStartX.current = null; return; }
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 40) { dx < 0 ? lbNext() : lbPrev(); }
    touchStartX.current = null;
  };
  const shortDesc = lang === "en"
    ? (product.short_description_en || product.short_description_it)
    : (product.short_description_it || product.short_description_en);
  const wygRaw = lang === "en"
    ? ((product.what_you_get_en || []).length ? product.what_you_get_en : product.what_you_get_it)
    : ((product.what_you_get_it || []).length ? product.what_you_get_it : product.what_you_get_en);
  const whatYouGet = (wygRaw || []).filter(Boolean);
  const pTitle = productTitle(product, lang);
  const pDescFull = stripHtml(productDesc(product, lang));
  const canonicalPath = `/prodotti/${product.id}`;
  const productLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: pTitle,
    description: pDescFull,
    ...(img ? { image: img } : {}),
    sku: product.id,
    brand: { "@type": "Brand", name: "LearnSphereHub" },
    offers: {
      "@type": "Offer",
      price: String(product.price ?? 0),
      priceCurrency: "EUR",
      availability: "https://schema.org/InStock",
      url: `${SITE_ORIGIN}${canonicalPath}`,
    },
  };
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_ORIGIN}/` },
      { "@type": "ListItem", position: 2, name: t("products.title"), item: `${SITE_ORIGIN}/prodotti` },
      { "@type": "ListItem", position: 3, name: pTitle, item: `${SITE_ORIGIN}${canonicalPath}` },
    ],
  };
  const add = () => { addItem(product, 1); toast.success(t("common.addToCart"), { description: productTitle(product, lang) }); };
  const buy = () => { addItem(product, 1); navigate("/carrello"); };

  const isAmazon = product.product_type === "amazon";
  const amazonKind = product.amazon_kind || "book";
  const isBook = amazonKind !== "product";
  const LV = (base) => (lang === "en" ? (product[`${base}_en`] || product[`${base}_it`]) : (product[`${base}_it`] || product[`${base}_en`])) || "";
  const author = LV("subtitle");
  const longDesc = LV("long_description");
  const whatsInside = LV("whats_inside");
  const forWhom = LV("for_whom");
  const highlights = LV("highlights").split("\n").map((s) => s.trim()).filter(Boolean);
  const fmtLabel = { kindle: "Kindle", paperback: "Cartaceo", both: "Kindle e Cartaceo" }[product.book_format] || "";
  const goAmazon = () => { if (product.amazon_url) window.open(product.amazon_url, "_blank", "noopener,noreferrer"); };
  const whatsInsideLabel = lang === "en"
    ? (isBook ? "What you get with this book" : "What you get with this product")
    : (isBook ? "Cosa trovi nel libro" : "Cosa trovi nel prodotto");
  const forWhomLabel = lang === "en" ? "Who it is for" : "Per chi è";
  const amazonBtnLabel = lang === "en" ? "View on Amazon" : "Vedi su Amazon";
  const affiliateNotice = lang === "en"
    ? "Purchase takes place on Amazon in a new window. LearnSphereHub may earn a commission from qualifying purchases at no extra cost to you."
    : "L'acquisto avviene su Amazon in una nuova finestra. LearnSphereHub può ricevere una commissione dagli acquisti idonei, senza costi aggiuntivi per te.";

  return (
    <div data-testid="product-detail-page" className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      <Seo
        title={pageTitle(LV("seo_title") || pTitle)}
        description={truncate(LV("seo_desc") || productDesc(product, lang))}
        path={canonicalPath}
        image={img || undefined}
        type="product"
        jsonLd={[breadcrumbLd, productLd]}
      />
      <Link to="/prodotti" data-testid="back-to-products" className="inline-flex items-center gap-2 text-sm text-brand-navy/60 hover:text-brand-electric transition-colors">
        <ArrowLeft className="h-4 w-4" /> {t("detail.back")}
      </Link>

      <div className="mt-6 grid lg:grid-cols-2 gap-10 items-start">
        <div>
          <div className="relative rounded-2xl overflow-hidden border border-brand-navy/10 bg-brand-light aspect-[4/3]">
            {mainImg ? (
              <button type="button" onClick={() => openLightbox(activeIdx)} data-testid="product-main-image-btn"
                className="group w-full h-full cursor-zoom-in" aria-label={lang === "en" ? "Enlarge image" : "Ingrandisci immagine"}>
                <img src={mainImg} alt={productTitle(product, lang)} className="w-full h-full object-contain p-4" data-testid="product-main-image" />
                <span className="absolute bottom-4 right-4 inline-flex items-center gap-1.5 rounded-full bg-brand-navy/70 text-white text-xs font-medium px-3 py-1.5 opacity-0 group-hover:opacity-100 transition-opacity backdrop-blur-sm">
                  <ZoomIn className="h-3.5 w-3.5" /> {lang === "en" ? "Zoom" : "Ingrandisci"}
                </span>
              </button>
            ) : <div className="w-full h-full flex items-center justify-center text-brand-navy/30"><ImageOff className="h-12 w-12" /></div>}
            {isAmazon && (
              <span data-testid="detail-amazon-badge" className="absolute top-4 left-4 bg-[#FF9900] text-brand-navy text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full">Amazon</span>
            )}
            {isAmazon && (
              <span data-testid="detail-kind-badge" className="absolute top-4 right-4 bg-white/95 text-brand-navy text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full border border-brand-navy/10 shadow-sm">
                {lang === "en" ? (isBook ? "Book" : "Product") : (isBook ? "Libro" : "Prodotto")}
              </span>
            )}
            {product.is_demo && !isAmazon && (
              <span data-testid="detail-demo-badge" className="absolute top-4 left-4 bg-brand-orange text-white text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full">{t("common.demo")}</span>
            )}
          </div>
          {allImgs.length > 1 && (
            <div className="mt-3 flex flex-wrap gap-3" data-testid="product-gallery-thumbs">
              {allImgs.map((im, i) => (
                <button key={i} type="button" onClick={() => setActiveIdx(i)} data-testid={`gallery-thumb-${i}`}
                  className={`h-20 w-20 rounded-lg overflow-hidden border-2 bg-brand-light shrink-0 transition-colors ${i === activeIdx ? "border-brand-electric" : "border-brand-navy/10 hover:border-brand-navy/30"}`}>
                  <img src={im} alt="" className="w-full h-full object-contain p-1" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <span className="text-xs uppercase tracking-widest text-brand-electric font-semibold">{t("detail.category")}: {product.category}</span>
          <h1 className="mt-3 text-3xl md:text-4xl font-extrabold font-heading text-brand-navy tracking-tight">{productTitle(product, lang)}</h1>
          {isAmazon && isBook && author && <p className="mt-2 text-lg text-brand-navy/60 italic">{author}</p>}
          {isAmazon && isBook && product.author && <p className="mt-1 text-sm text-brand-navy/70">di <span className="font-semibold">{product.author}</span></p>}
          {isAmazon ? (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {isBook && fmtLabel && <span className="inline-flex items-center rounded-full bg-brand-navy/5 px-3 py-1 text-xs font-semibold text-brand-navy">{fmtLabel}</span>}
              {product.price > 0 && <span className="font-heading font-bold text-2xl text-brand-navy">{formatPrice(product.price)}</span>}
              {product.price > 0 && <span className="text-xs text-brand-navy/50">prezzo indicativo</span>}
            </div>
          ) : (
            <div className="mt-4 font-heading font-bold text-3xl text-brand-navy">{formatPrice(product.price)}</div>
          )}

          {!isAmazon && shortDesc && (
            <p data-testid="product-short-desc" className="mt-4 text-brand-navy/75 leading-relaxed">{shortDesc}</p>
          )}

          <div className="mt-6">
            <h3 className="font-semibold text-brand-navy">{t("detail.description")}</h3>
            <p className="mt-2 text-brand-navy/70 leading-relaxed whitespace-pre-line">{longDesc || productDesc(product, lang)}</p>
          </div>

          {!isAmazon && whatYouGet.length > 0 && (
            <div className="mt-6" data-testid="product-what-you-get">
              <h3 className="font-semibold text-brand-navy">{lang === "en" ? "What you will receive" : "Cosa riceverai"}</h3>
              <ul className="mt-3 space-y-2">
                {whatYouGet.map((w, i) => (
                  <li key={i} data-testid={`wyg-item-${i}`} className="flex items-start gap-2 text-sm text-brand-navy/75"><Check className="h-4 w-4 text-brand-electric shrink-0 mt-0.5" /> {w}</li>
                ))}
              </ul>
            </div>
          )}

          {isAmazon && whatsInside && (
            <div className="mt-6" data-testid="amazon-whats-inside">
              <h3 className="font-semibold text-brand-navy">{whatsInsideLabel}</h3>
              <p className="mt-2 text-brand-navy/70 leading-relaxed whitespace-pre-line">{whatsInside}</p>
            </div>
          )}
          {isAmazon && forWhom && (
            <div className="mt-6" data-testid="amazon-for-whom">
              <h3 className="font-semibold text-brand-navy">{forWhomLabel}</h3>
              <p className="mt-2 text-brand-navy/70 leading-relaxed whitespace-pre-line">{forWhom}</p>
            </div>
          )}
          {isAmazon && highlights.length > 0 && (
            <ul className="mt-6 space-y-2" data-testid="amazon-highlights">
              {highlights.map((h, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-brand-navy/75"><Check className="h-4 w-4 text-brand-electric shrink-0 mt-0.5" /> {h}</li>
              ))}
            </ul>
          )}

          {product.is_demo && !isAmazon && (
            <div className="mt-6 flex items-start gap-3 rounded-xl bg-brand-orange/10 border border-brand-orange/20 p-4 text-sm text-brand-navy/80">
              <Info className="h-5 w-5 text-brand-orange shrink-0 mt-0.5" />
              <span>{t("detail.demoNotice")}</span>
            </div>
          )}

          {isAmazon ? (
            <div className="mt-8">
              <Button data-testid="detail-amazon-btn" onClick={goAmazon} disabled={!product.amazon_url}
                className="rounded-full h-12 px-8 bg-[#FF9900] hover:bg-[#e88b00] text-brand-navy font-bold gap-2">
                <ExternalLink className="h-4 w-4" /> {amazonBtnLabel}
              </Button>
              <p data-testid="amazon-affiliate-notice" className="mt-3 flex items-start gap-2 text-xs text-brand-navy/50"><Info className="h-4 w-4 shrink-0 mt-0.5" /> {affiliateNotice}</p>
            </div>
          ) : (
            <>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button data-testid="detail-add-cart" onClick={add} variant="outline" className="rounded-full h-12 px-7 border-brand-navy/20 text-brand-navy gap-2">
                  <ShoppingCart className="h-4 w-4" /> {t("common.addToCart")}
                </Button>
                <Button data-testid="detail-buy-now" onClick={buy} className="rounded-full h-12 px-8 bg-brand-electric hover:bg-brand-electric/90 text-white gap-2">
                  {t("common.buyNow")}
                </Button>
              </div>
              {product.has_file && (
                <p className="mt-4 inline-flex items-center gap-2 text-sm text-brand-navy/50"><Download className="h-4 w-4" /> {product.file_name}</p>
              )}
              {product.has_preview && (
                <div className="mt-4">
                  <button type="button" onClick={() => setPreviewOpen(true)} data-testid="product-preview-btn"
                    className="inline-flex items-center gap-2 rounded-full border border-brand-electric/40 bg-brand-electric/5 px-5 py-2.5 text-sm font-semibold text-brand-electric hover:bg-brand-electric/10 transition-colors">
                    <FileText className="h-4 w-4" /> {lang === "en" ? "Browse the preview" : "Sfoglia l'anteprima"}
                  </button>
                  <p className="mt-1.5 text-xs text-brand-navy/45">{lang === "en" ? "First 2 pages · watermarked preview" : "Prime 2 pagine · anteprima con filigrana"}</p>
                </div>
              )}
              <div data-testid="secure-payment-note" className="mt-5 rounded-xl border border-brand-navy/10 bg-brand-light/60 px-4 py-3">
                <p className="flex items-center gap-2 text-sm font-semibold text-brand-navy">
                  <ShieldCheck className="h-4 w-4 text-brand-electric" /> {lang === "en" ? "Secure payment with Stripe" : "Pagamento sicuro con Stripe"}
                </p>
                <p className="mt-1 text-xs text-brand-navy/60">
                  {lang === "en"
                    ? "Protected transaction • Download after payment • LearnSphereHub support"
                    : "Transazione protetta • Download dopo il pagamento • Assistenza LearnSphereHub"}
                </p>
                <p className="mt-1 text-xs text-brand-navy/45">
                  {lang === "en"
                    ? "Full card details are not handled directly by LearnSphereHub."
                    : "I dati completi della carta non vengono gestiti direttamente da LearnSphereHub."}
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      {lightbox && allImgs.length > 0 && (
        <div data-testid="product-lightbox" onClick={() => setLightbox(false)}
          onTouchStart={onLbTouchStart} onTouchEnd={onLbTouchEnd}
          className="fixed inset-0 z-[100] bg-brand-navy/95 backdrop-blur-sm flex items-center justify-center animate-in fade-in">
          <button type="button" data-testid="lightbox-close" onClick={(e) => { e.stopPropagation(); setLightbox(false); }}
            className="absolute top-5 right-5 h-11 w-11 inline-flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors">
            <X className="h-6 w-6" />
          </button>
          {allImgs.length > 1 && (
            <>
              <button type="button" data-testid="lightbox-prev" onClick={lbPrev}
                className="absolute left-4 md:left-8 h-12 w-12 inline-flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors">
                <ChevronLeft className="h-7 w-7" />
              </button>
              <button type="button" data-testid="lightbox-next" onClick={lbNext}
                className="absolute right-4 md:right-8 h-12 w-12 inline-flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors">
                <ChevronRight className="h-7 w-7" />
              </button>
            </>
          )}
          <img src={allImgs[lbIdx]} alt={productTitle(product, lang)} data-testid="lightbox-image"
            onClick={(e) => { e.stopPropagation(); setZoomed((z) => !z); }}
            className={`max-h-[88vh] max-w-[92vw] object-contain transition-transform duration-300 ${zoomed ? "scale-150 cursor-zoom-out" : "scale-100 cursor-zoom-in"}`} />
          {allImgs.length > 1 && (
            <div className="absolute bottom-5 left-1/2 -translate-x-1/2 text-white/80 text-sm font-medium bg-white/10 rounded-full px-3 py-1" data-testid="lightbox-counter">
              {lbIdx + 1} / {allImgs.length}
            </div>
          )}
        </div>
      )}

      {previewOpen && product.has_preview && (
        <div data-testid="product-preview-modal" onClick={() => setPreviewOpen(false)}
          className="fixed inset-0 z-[100] bg-brand-navy/80 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6 animate-in fade-in">
          <div onClick={(e) => e.stopPropagation()}
            className="bg-white w-full max-w-3xl max-h-[92vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden">
            <div className="flex items-center justify-between gap-3 px-4 sm:px-5 py-3 border-b border-brand-navy/10 shrink-0">
              <p className="text-sm font-semibold text-brand-navy flex items-center gap-2">
                <FileText className="h-4 w-4 text-brand-electric" />
                {lang === "en" ? "Preview · first 2 pages" : "Anteprima · prime 2 pagine"}
              </p>
              <button type="button" onClick={() => setPreviewOpen(false)} data-testid="preview-modal-close"
                className="h-9 w-9 inline-flex items-center justify-center rounded-full text-brand-navy/60 hover:bg-brand-navy/5 hover:text-brand-navy transition-colors">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="overflow-y-auto p-3 sm:p-4 bg-brand-light/50 space-y-4">
              {Array.from({ length: product.preview_pages || 2 }, (_, i) => i + 1).map((pg) => (
                <ZoomablePage key={pg} src={`${API}/products/${product.id}/preview-page/${pg}.png`}
                  testid={`preview-page-${pg}`} alt={`${lang === "en" ? "Preview page" : "Pagina anteprima"} ${pg}`} />
              ))}
            </div>
            <div className="px-4 sm:px-5 py-2.5 border-t border-brand-navy/10 shrink-0 flex flex-col items-center gap-1">
              <p className="text-[11px] text-brand-navy/45">{lang === "en" ? "Pinch to zoom · double-tap to reset" : "Pizzica per ingrandire · doppio tap per ripristinare"}</p>
              <button type="button" onClick={() => setPreviewOpen(false)} data-testid="preview-modal-close-bottom"
                className="text-sm font-medium text-brand-navy/60 hover:text-brand-navy">
                {lang === "en" ? "Close preview" : "Chiudi anteprima"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
